import { useMemo, useState } from "react";
import type { MonthlyYoy } from "@/lib/api";
import { lastFullMonth } from "@/lib/dates";
import { fmtInt, fmtUSDCompact, fmtDelta, deltaTone } from "@/lib/format";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type Metric = "revenue" | "qty";

function pct(cur: number, prior: number): number | null {
  if (!(prior > 0)) return null;
  return ((cur - prior) / prior) * 100;
}

export default function MonthlyYoyTable({ data }: { data: MonthlyYoy }) {
  const [metric, setMetric] = useState<Metric>("revenue");
  const fmt = metric === "revenue" ? fmtUSDCompact : fmtInt;

  const { years } = data;

  // (year, month) -> value for the selected metric
  const valueAt = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data.cells) {
      m.set(`${c.year}-${c.month}`, metric === "revenue" ? c.revenue : c.qty);
    }
    return (year: number, month: number) => m.get(`${year}-${month}`) ?? null;
  }, [data.cells, metric]);

  // Per-year full-year totals, plus YoY against the prior year. The current
  // (incomplete) year is compared like-for-like and flagged "YTD": Jan through
  // the last month that actually has data this year, vs the same months last
  // year. Capping at the last month with data (not the calendar last-full
  // month) keeps the comparison honest when ingestion lags.
  const summary = useMemo(() => {
    const lf = lastFullMonth(new Date());
    const currentYear = new Date().getUTCFullYear();

    const sumThrough = (year: number, lastMonth: number) => {
      let s = 0;
      for (let mo = 1; mo <= lastMonth; mo++) s += valueAt(year, mo) ?? 0;
      return s;
    };
    const lastMonthWithData = (year: number) => {
      let mx = 0;
      for (let mo = 1; mo <= 12; mo++) if (valueAt(year, mo) != null) mx = mo;
      return mx;
    };

    return years.map((year) => {
      const total = sumThrough(year, 12);
      const isCurrentIncomplete = year === currentYear && lf.year === currentYear;
      const hasPrior = years.includes(year - 1);

      let yoy: number | null = null;
      let ytd = false;
      if (hasPrior) {
        if (isCurrentIncomplete) {
          ytd = true;
          const boundary = Math.min(lf.month, lastMonthWithData(year));
          if (boundary >= 1) {
            yoy = pct(sumThrough(year, boundary), sumThrough(year - 1, boundary));
          }
        } else {
          yoy = pct(total, sumThrough(year - 1, 12));
        }
      }
      return { year, total, yoy, ytd, incomplete: isCurrentIncomplete };
    });
  }, [years, valueAt]);

  if (years.length === 0) {
    return <p className="text-sm text-ink-500">No sales history for this customer.</p>;
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-ink-500">All products · full history</span>
        <div className="flex gap-1.5">
          {(["revenue", "qty"] as Metric[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                metric === m
                  ? "border-ink-900 bg-ink-900 text-white"
                  : "border-ink-200 bg-white text-ink-600 hover:border-ink-300"
              }`}
            >
              {m === "revenue" ? "Revenue" : "Quantity"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="py-2 pr-4 text-left font-medium">Month</th>
              {years.map((y) => (
                <th key={y} className="py-2 pl-4 text-right font-medium tabular-nums">
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200">
            {MONTHS.map((label, i) => {
              const month = i + 1;
              return (
                <tr key={label}>
                  <td className="py-1.5 pr-4 text-ink-700">{label}</td>
                  {years.map((y) => {
                    const v = valueAt(y, month);
                    return (
                      <td
                        key={y}
                        className={`py-1.5 pl-4 text-right tabular-nums ${
                          v == null ? "text-ink-300" : "text-ink-900"
                        }`}
                      >
                        {v == null ? "—" : fmt(v)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink-300 font-semibold">
              <td className="py-2 pr-4 text-ink-900">Total</td>
              {summary.map((s) => (
                <td key={s.year} className="py-2 pl-4 text-right tabular-nums text-ink-900">
                  {fmt(s.total)}
                </td>
              ))}
            </tr>
            <tr className="text-xs">
              <td className="py-2 pr-4 text-ink-500">YoY</td>
              {summary.map((s) => {
                const tone = deltaTone(s.yoy);
                const toneClass =
                  tone === "up" ? "text-success" : tone === "down" ? "text-danger" : "text-ink-500";
                return (
                  <td key={s.year} className={`py-2 pl-4 text-right tabular-nums ${toneClass}`}>
                    {fmtDelta(s.yoy)}
                    {s.ytd && s.yoy != null ? <span className="ml-1 text-ink-400">YTD</span> : null}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
